import React, { useCallback, useEffect, useState } from 'react';
import { BoxIcon, SearchIcon, PlusIcon, DollarSignIcon, ClockIcon, EditIcon, TrashIcon, UserIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { StatsCard } from '../components/ui/StatsCard';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { EmptyBoxPurchase } from '../types';
import { supabaseAdmin } from '../lib/supabase';
import { useSortableData } from '../hooks/useSortableData';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
import { useAuth } from '../context/AuthContext';
const VENDEDORES = ['Juan Pérez', 'María García', 'Carlos Ruiz', 'Ana López'];
const FARMACIAS = ['Farmacia Central', 'Farmacia El Sol', 'Farmacia La Salud', 'Farmacia Bienestar', 'Farmacia Nueva Vida'];
const INITIAL_PURCHASES: EmptyBoxPurchase[] = [{
  id: '1',
  farmacia: 'Farmacia Central',
  vendedor: 'Juan Pérez',
  fecha: new Date('2024-02-28'),
  cantidadCajas: 50,
  precioPorCaja: 25,
  total: 1250,
  estado: 'pagado'
}, {
  id: '2',
  farmacia: 'Farmacia El Sol',
  vendedor: 'María García',
  fecha: new Date('2024-02-27'),
  cantidadCajas: 30,
  precioPorCaja: 25,
  total: 750,
  estado: 'pendiente'
}, {
  id: '3',
  farmacia: 'Farmacia La Salud',
  vendedor: 'Carlos Ruiz',
  fecha: new Date('2024-02-26'),
  cantidadCajas: 100,
  precioPorCaja: 22,
  total: 2200,
  estado: 'pagado'
}, {
  id: '4',
  farmacia: 'Farmacia Bienestar',
  vendedor: 'Juan Pérez',
  fecha: new Date('2024-02-25'),
  cantidadCajas: 45,
  precioPorCaja: 25,
  total: 1125,
  estado: 'pagado'
}, {
  id: '5',
  farmacia: 'Farmacia Nueva Vida',
  vendedor: 'Ana López',
  fecha: new Date('2024-02-24'),
  cantidadCajas: 20,
  precioPorCaja: 25,
  total: 500,
  estado: 'pendiente'
}];
type FormData = {
  farmacia: string;
  vendedor: string;
  fecha: string;
  cantidadCajas: string;
  precioPorCaja: string;
  estado: EmptyBoxPurchase['estado'];
  nota: string;
};
const emptyForm: FormData = {
  farmacia: FARMACIAS[0],
  vendedor: VENDEDORES[0],
  fecha: new Date().toISOString().split('T')[0],
  cantidadCajas: '',
  precioPorCaja: '',
  estado: 'pendiente',
  nota: ''
};
export function CajaVaciaPage() {
  const {
    addActivityLog
  } = useAuth();
  const [purchases, setPurchases] = useState<EmptyBoxPurchase[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendedorFilter, setVendedorFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState<EmptyBoxPurchase | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const filteredPurchases = purchases.filter(p => {
    const matchesSearch = p.farmacia.toLowerCase().includes(searchQuery.toLowerCase()) || p.vendedor.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.estado === statusFilter;
    const matchesVendedor = vendedorFilter === 'all' || p.vendedor === vendedorFilter;
    return matchesSearch && matchesStatus && matchesVendedor;
  });
  // Sorting setup
  const {
    items: sortedPurchases,
    requestSort,
    sortConfig
  } = useSortableData(filteredPurchases, {
    farmacia: p => p.farmacia,
    vendedor: p => p.vendedor,
    fecha: p => new Date(p.fecha).getTime(),
    cantidadCajas: p => p.cantidadCajas,
    precioPorCaja: p => p.precioPorCaja,
    total: p => p.total,
    estado: p => p.estado
  });
  // Default sort by date descending if no sort config
  const finalPurchases = sortConfig ? sortedPurchases : [...sortedPurchases].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  const totalCajas = finalPurchases.reduce((sum, p) => sum + p.cantidadCajas, 0);
  const inversionTotal = finalPurchases.reduce((sum, p) => sum + p.total, 0);
  const comprasPendientes = finalPurchases.filter(p => p.estado === 'pendiente').length;
  const computedTotal = (Number(formData.cantidadCajas) || 0) * (Number(formData.precioPorCaja) || 0);
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0
  }).format(amount);
  const formatDate = (date: Date) => new Date(date).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  const openAddModal = () => {
    setEditingPurchase(null);
    setFormData(emptyForm);
    setIsModalOpen(true);
  };
  const openEditModal = (purchase: EmptyBoxPurchase) => {
    setEditingPurchase(purchase);
    setFormData({
      farmacia: purchase.farmacia,
      vendedor: purchase.vendedor,
      fecha: new Date(purchase.fecha).toISOString().split('T')[0],
      cantidadCajas: String(purchase.cantidadCajas),
      precioPorCaja: String(purchase.precioPorCaja),
      estado: purchase.estado,
      nota: purchase.nota || ''
    });
    setIsModalOpen(true);
  };
  useEffect(() => {
    loadPurchases();
  }, []);
  // Auto-refresh when empty_box_purchases change in Supabase
  const loadPurchasesCallback = useCallback(() => {
    loadPurchases();
  }, []);
  useRealtimeRefresh(['empty_box_purchases'], loadPurchasesCallback);
  const mapPurchase = (row: any): EmptyBoxPurchase => ({
    id: row.id,
    farmacia: row.farmacia,
    vendedor: row.vendedor,
    fecha: new Date(row.fecha),
    cantidadCajas: row.cantidad_cajas,
    precioPorCaja: row.precio_por_caja,
    total: row.total,
    estado: row.estado,
    nota: row.nota || undefined
  });
  const loadPurchases = async () => {
    setIsLoading(true);
    try {
      const {
        data
      } = await supabaseAdmin.from('empty_box_purchases').select('*').order('fecha', {
        ascending: false
      });
      if (data) setPurchases(data.map(mapPurchase));
    } finally {
      setIsLoading(false);
    }
  };
  const handleSave = async () => {
    if (!formData.cantidadCajas || !formData.precioPorCaja) return;
    setIsSaving(true);
    try {
      const payload = {
        farmacia: formData.farmacia,
        vendedor: formData.vendedor,
        fecha: new Date(formData.fecha).toISOString(),
        cantidad_cajas: Number(formData.cantidadCajas),
        precio_por_caja: Number(formData.precioPorCaja),
        total: computedTotal,
        estado: formData.estado,
        nota: formData.nota || null
      };
      if (editingPurchase) {
        const {
          data
        } = await supabaseAdmin.from('empty_box_purchases').update(payload).eq('id', editingPurchase.id).select().single();
        if (data) setPurchases(prev => prev.map(p => p.id === editingPurchase.id ? mapPurchase(data) : p));
        addActivityLog('Editó compra caja vacía', `Farmacia: ${formData.farmacia} | Cajas: ${formData.cantidadCajas} | Total: RD${computedTotal.toFixed(2)}`);
      } else {
        const {
          data
        } = await supabaseAdmin.from('empty_box_purchases').insert(payload).select().single();
        if (data) setPurchases(prev => [mapPurchase(data), ...prev]);
        addActivityLog('Creó compra caja vacía', `Farmacia: ${formData.farmacia} | Cajas: ${formData.cantidadCajas} | Total: RD${computedTotal.toFixed(2)}`);
      }
      setIsModalOpen(false);
    } finally {
      setIsSaving(false);
    }
  };
  const handleDelete = async () => {
    if (!editingPurchase) return;
    addActivityLog('Eliminó compra caja vacía', `Farmacia: ${editingPurchase.farmacia} | Total: RD${editingPurchase.total.toFixed(2)}`);
    await supabaseAdmin.from('empty_box_purchases').delete().eq('id', editingPurchase.id);
    setPurchases(prev => prev.filter(p => p.id !== editingPurchase.id));
    setIsDeleteModalOpen(false);
    setIsModalOpen(false);
  };
  return <div className="space-y-6" data-id="element-457" data-mp="55kb5a2s1c97">
      <PageHeader title="Compra de Caja Vacía" subtitle="Registro de compra de cajas a farmacias" icon={<BoxIcon className="w-6 h-6" data-id="element-459" data-mp="xn675h3xa49k" />} data-id="element-458" data-mp="54sfy0d3dgjg">
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-461" data-mp="xpak0ojsudlz" />} onClick={openAddModal} data-id="element-460" data-mp="xp0pcnnt7i9y">
          Registrar Compra
        </Button>
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-stagger-1" data-id="element-462" data-mp="116y8qa9j4tqa">
        <StatsCard title="Total Cajas" value={totalCajas} icon={<BoxIcon className="w-5 h-5" data-id="element-464" data-mp="1a5sb6h9jkebr" />} variant="primary" data-id="element-463" data-mp="15807yr3g55s9" />
        <StatsCard title="Inversión Total" value={formatCurrency(inversionTotal)} icon={<DollarSignIcon className="w-5 h-5" data-id="element-466" data-mp="1k4ra384i8vlk" />} variant="success" data-id="element-465" data-mp="1f4v6vb3mt872" />
        <StatsCard title="Pagos Pendientes" value={comprasPendientes} icon={<ClockIcon className="w-5 h-5" data-id="element-468" data-mp="1swjf33arfcir" />} variant="warning" data-id="element-467" data-mp="1o5t8xnuif5n0" />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 animate-stagger-2" data-id="element-469" data-mp="1xw5unje294uq">
        <div className="relative flex-1" data-id="element-470" data-mp="1xxh15qyw4qpj">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-471" data-mp="22vs8nv1pqp4l" />
          <input type="text" placeholder="Buscar por farmacia o vendedor..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4]" data-id="element-472" data-mp="27uvcjc5yipaz" />
        </div>
        <select value={vendedorFilter} onChange={e => setVendedorFilter(e.target.value)} className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-473" data-mp="1hu4cxup2vijw">
          <option value="all" data-id="element-474" data-mp="1vtvprs3uyswb">Todos los vendedores</option>
          {VENDEDORES.map(v => <option key={v} value={v} data-id="element-475" data-mp="20sgmvintcwgj">
              {v}
            </option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-476" data-mp="1g4fqrhma70c">
          <option value="all" data-id="element-477" data-mp="3q9uel5ohzauy">Todos los estados</option>
          <option value="pendiente" data-id="element-478" data-mp="3q89ihbmjbjuz">Pendiente</option>
          <option value="pagado" data-id="element-479" data-mp="3lahp0d68rpoo">Pagado</option>
        </select>
      </div>

      {/* Table */}
      <Card padding="none" className="animate-stagger-3" data-id="element-480" data-mp="3gby2obnqkbvs">
        {filteredPurchases.length === 0 ? <div className="p-16 text-center animate-fade-in" data-id="element-481" data-mp="htslz15vay3r">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-482" data-mp="hry7o5p3q48n">
              <BoxIcon className="w-8 h-8 text-slate-400" data-id="element-483" data-mp="ct4uyfhn2h9j" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-484" data-mp="7u1nkoe6ryye">
              No hay registros
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-485" data-mp="3wm1n25hwrcf">
              No se encontraron compras con los filtros aplicados
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-486" data-mp="2ojpahjyyi5p0">
            <table className="w-full" data-id="element-487" data-mp="2q1nxu4dbizrx">
              <thead data-id="element-488" data-mp="2q2p8i7qf9k94">
                <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-489" data-mp="2q273alls7vp2">
                  <SortableTableHeader label="Farmacia" sortKey="farmacia" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-490" data-mp="2uzpeyjr6op0r" />
                  <SortableTableHeader label="Vendedor" sortKey="vendedor" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-491" data-mp="27aweqb3j3v0c" />
                  <SortableTableHeader label="Fecha" sortKey="fecha" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-492" data-mp="22bjvs1fs3831" />
                  <SortableTableHeader label="Cajas" sortKey="cantidadCajas" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-493" data-mp="19plcyr20xkog" />
                  <SortableTableHeader label="Precio/Caja" sortKey="precioPorCaja" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-494" data-mp="1dqx6v8rxks2g" />
                  <SortableTableHeader label="Total" sortKey="total" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-495" data-mp="21fzoigmkekwy" />
                  <SortableTableHeader label="Estado" sortKey="estado" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-496" data-mp="26drirhaomj4b" />
                  <th className="px-5 py-4 w-20" data-id="element-497" data-mp="3493ubivqnfkn"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-498" data-mp="27j4csyxm16px">
                {finalPurchases.map(purchase => <tr key={purchase.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-499" data-mp="2bk6ahbbvpaps">
                    <td className="px-5 py-4 font-medium text-slate-900" data-id="element-500" data-mp="2gihm9rlqhvan">
                      {purchase.farmacia}
                    </td>
                    <td className="px-5 py-4" data-id="element-501" data-mp="3rz7u1f5t2v6d">
                      <div className="flex items-center gap-1.5 text-sm text-slate-600" data-id="element-502" data-mp="3ryfyg4c7kxg3">
                        <UserIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-503" data-mp="rdcbrb89hcq" />
                        {purchase.vendedor}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600" data-id="element-504" data-mp="1gb2tpg1tofnv">
                      {formatDate(purchase.fecha)}
                    </td>
                    <td className="px-5 py-4 text-center font-medium text-slate-900" data-id="element-505" data-mp="1bj2yg0wp6axm">
                      {purchase.cantidadCajas}
                    </td>
                    <td className="px-5 py-4 text-right text-sm text-slate-600" data-id="element-506" data-mp="12khga6v50xxd">
                      {formatCurrency(purchase.precioPorCaja)}
                    </td>
                    <td className="px-5 py-4 text-right font-bold text-slate-900" data-id="element-507" data-mp="sm1qc77ujupg">
                      {formatCurrency(purchase.total)}
                    </td>
                    <td className="px-5 py-4 text-center" data-id="element-508" data-mp="noi34ht687ld">
                      {purchase.estado === 'pagado' ? <Badge variant="success" data-id="element-509" data-mp="2mxyi61a7b2l1">Pagado</Badge> : <Badge variant="warning" data-id="element-510" data-mp="2hyveol9vd4f6">Pendiente</Badge>}
                    </td>
                    <td className="px-5 py-4" data-id="element-511" data-mp="g9z93h8ks7k3">
                      <div className="flex items-center gap-1" data-id="element-512" data-mp="l3kazkdahlok">
                        <button onClick={() => openEditModal(purchase)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-513" data-mp="6dmfskgxzvpv">
                          <EditIcon className="w-4 h-4" data-id="element-514" data-mp="z2tfvnw1otd8" />
                        </button>
                        <button onClick={() => {
                    setEditingPurchase(purchase);
                    setIsDeleteModalOpen(true);
                  }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-515" data-mp="14020dv4o19hn">
                          <TrashIcon className="w-4 h-4" data-id="element-516" data-mp="3etxs5uu0xjeb" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </Card>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingPurchase ? 'Editar Compra' : 'Registrar Compra de Caja Vacía'} size="md" data-id="element-517" data-mp="3h831k7uwylkq">
        <ModalBody data-id="element-518" data-mp="2oim9xcf4b8pk">
          <div className="space-y-4" data-id="element-519" data-mp="2ok7430xispmw">
            <div className="grid grid-cols-2 gap-4" data-id="element-520" data-mp="2oh2s3hdtcplo">
              <div data-id="element-521" data-mp="2jldpf56hysl5">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-522" data-mp="2jlvsakno0521">
                  Farmacia
                </label>
                <select value={formData.farmacia} onChange={e => setFormData({
                ...formData,
                farmacia: e.target.value
              })} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-523" data-mp="2tiig9ibafc8a">
                  {FARMACIAS.map(f => <option key={f} value={f} data-id="element-524" data-mp="1kbpbu00koek8">
                      {f}
                    </option>)}
                </select>
              </div>
              <div data-id="element-525" data-mp="1tb2jcn54yt2b">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-526" data-mp="1tbm4sdtzvzl3">
                  Vendedor
                </label>
                <select value={formData.vendedor} onChange={e => setFormData({
                ...formData,
                vendedor: e.target.value
              })} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-527" data-mp="23878mvua34nc">
                  {VENDEDORES.map(v => <option key={v} value={v} data-id="element-528" data-mp="2lwm2dqvzcwru">
                      {v}
                    </option>)}
                </select>
              </div>
            </div>
            <Input label="Fecha" type="date" value={formData.fecha} onChange={e => setFormData({
            ...formData,
            fecha: e.target.value
          })} data-id="element-529" data-mp="5emol62ziyip" />
            <div className="grid grid-cols-2 gap-4" data-id="element-530" data-mp="t1b8fxbk111y">
              <Input label="Cantidad de Cajas" type="number" value={formData.cantidadCajas} onChange={e => setFormData({
              ...formData,
              cantidadCajas: e.target.value
            })} placeholder="0" data-id="element-531" data-mp="ozzkdw8bwnvv" />
              <Input label="Precio por Caja (DOP)" type="number" value={formData.precioPorCaja} onChange={e => setFormData({
              ...formData,
              precioPorCaja: e.target.value
            })} placeholder="0" data-id="element-532" data-mp="17zdgajpmc8p0" />
            </div>
            {/* Auto-calculated total */}
            {computedTotal > 0 && <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200" data-id="element-533" data-mp="1arfq4hwphm84">
                <span className="text-sm font-medium text-slate-600" data-id="element-534" data-mp="23e0eralsrk7z">
                  Total calculado:
                </span>
                <span className="text-lg font-bold text-slate-900" data-id="element-535" data-mp="1ymiekorllno2">
                  {formatCurrency(computedTotal)}
                </span>
              </div>}
            <div data-id="element-536" data-mp="2r3l0205fatkf">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-537" data-mp="2r44kpomgjw25">
                Estado
              </label>
              <select value={formData.estado} onChange={e => setFormData({
              ...formData,
              estado: e.target.value as EmptyBoxPurchase['estado']
            })} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-538" data-mp="2mc2yb9vksoye">
                <option value="pendiente" data-id="element-539" data-mp="3dkcvgnz82rd8">Pendiente</option>
                <option value="pagado" data-id="element-540" data-mp="3djv342fokxal">Pagado</option>
              </select>
            </div>
            <div data-id="element-541" data-mp="3hknbktgqay5t">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-542" data-mp="3hk59hg7dxppz">
                Nota (opcional)
              </label>
              <input type="text" value={formData.nota} onChange={e => setFormData({
              ...formData,
              nota: e.target.value
            })} placeholder="Observaciones..." className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-543" data-mp="3rbi064hg69uf" />
            </div>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-544" data-mp="3lbv6bel3voql">
          <Button variant="secondary" onClick={() => setIsModalOpen(false)} data-id="element-545" data-mp="3ld66l4fceecy">
            Cancelar
          </Button>
          <Button onClick={handleSave} isLoading={isSaving} disabled={!formData.cantidadCajas || !formData.precioPorCaja} data-id="element-546" data-mp="3gitksnqe8qy1">
            {editingPurchase ? 'Guardar Cambios' : 'Registrar Compra'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirm */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Eliminar Registro" size="sm" data-id="element-547" data-mp="36076nh6xn20">
        <ModalBody data-id="element-548" data-mp="29wzmzfbikz3p">
          <p className="text-slate-600" data-id="element-549" data-mp="29yathmwcgkyi">
            ¿Eliminar la compra de <strong data-id="element-550" data-mp="24yodx6t1msmj">{editingPurchase?.farmacia}</strong>?
            Esta acción no se puede deshacer.
          </p>
        </ModalBody>
        <ModalFooter data-id="element-551" data-mp="khopkoj0mzzt">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)} data-id="element-552" data-mp="pe5nubjxiuqm">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete} data-id="element-553" data-mp="ap97zsbmsdfg">
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}